import { Card, PostMiniCardBase } from "./post-mini-card";
import { useState } from "react";
import { Button } from "../../../../components/ui/button";
import { Spinner } from "../../../../components/ui/spinner";
import { useCurrentUser } from "../../../../entities/current-user";
import { getBulkCreateLimit } from "../../bulk-create/limit-config";
import { VirtualizedMiniCardList } from "./virtualized-mini-card-list";

export const PostMiniCardTabs = ({
  cards,
  onSelect,
  onCloseAll,
  onClose,
  onAddNewForm,
  showAddNewButton,
  onDuplicate,
  onSaveAll,
  isSavingAll,
}: {
  cards: Card[];
  onSelect: (card: Card) => void;
  onCloseAll: () => void;
  onClose: (card: Card) => void;
  onAddNewForm: () => void;
  showAddNewButton?: boolean;
  onDuplicate?: (card: Card) => void;
  onSaveAll?: () => void;
  isSavingAll?: boolean;
}) => {
  const [collapsedClusterIds, setCollapsedClusterIds] = useState<
    (string | undefined)[]
  >([]);
  const currentUser = useCurrentUser();
  const clusters = partitionByCluster(cards);
  const isReachedMaxFormCount =
    cards.length >= getBulkCreateLimit(currentUser as any);
  return (
    <div
      data-testid="post-mini-card-tabs"
      className="flex h-full w-[224px] shrink-0 flex-col gap-[8px] overflow-x-hidden overflow-y-auto rounded-l-[8px] p-[8px]"
      style={{
        backgroundColor: "#f4f2ff",
        boxShadow:
          "0 2px 4px -2px rgba(16, 24, 40, 0.06), 0 4px 8px -2px rgba(16, 24, 40, 0.1)",
      }}
    >
      <div className="flex gap-[8px]">
        <Button
          variant="black-secondary"
          size="xs"
          className="h-7 flex-1 rounded-lg border-white px-2 text-xs text-[#333] shadow-[0_1px_3px_rgba(16,24,40,0.1),0_1px_2px_rgba(16,24,40,0.06)] [&_i]:inline-flex [&_i]:size-4 [&_i]:items-center [&_i]:justify-center [&_i]:text-[14px]"
          onClick={onCloseAll}
        >
          <i className="fa-regular fa-xmark" /> Discard all
        </Button>
        {onSaveAll && (
          <Button
            variant="black-secondary"
            size="xs"
            className="h-7 flex-1 rounded-lg border-white px-2 text-xs text-[#333] shadow-[0_1px_3px_rgba(16,24,40,0.1),0_1px_2px_rgba(16,24,40,0.06)] [&_i]:inline-flex [&_i]:size-4 [&_i]:items-center [&_i]:justify-center [&_i]:text-[14px]"
            onClick={onSaveAll}
            disabled={isSavingAll}
          >
            {isSavingAll ? (
              <span className="flex items-center gap-1.5">
                <Spinner size="sm" />
                Saving...
              </span>
            ) : (
              <>
                <i className="fa-regular fa-floppy-disk" /> Save all
              </>
            )}
          </Button>
        )}
      </div>
      {showAddNewButton && (
        <Button
          variant="black-secondary"
          onClick={() => {
            onAddNewForm();
          }}
          name="plus"
          size="sm"
          className="h-8 w-full rounded-lg border-white bg-white px-3 py-1.5 text-sm text-[#333] shadow-[0_1px_3px_rgba(16,24,40,0.1),0_1px_2px_rgba(16,24,40,0.06)] [&_i]:inline-flex [&_i]:size-5 [&_i]:items-center [&_i]:justify-center [&_i]:text-[16px]"
          disabled={isReachedMaxFormCount}
        >
          <i className="fa-regular fa-plus" /> New post
        </Button>
      )}
      {clusters.map((cards, idx) => {
        // For large lists (10+ cards), use virtualization
        if (cards.length >= 10) {
          const selectedIndex = cards.findIndex((card) => card.isSelected);
          return (
            <div
              className="flex flex-col gap-[8px]"
              key={cards[0]?.clusterId || idx}
            >
              <VirtualizedMiniCardList
                cards={cards}
                onSelect={onSelect}
                onClose={onClose}
                onDuplicate={onDuplicate}
                isReachedMaxFormCount={isReachedMaxFormCount}
                selectedCardIndex={selectedIndex}
                isClustered
              />
            </div>
          );
        }

        // For small lists (1-9 cards), render normally
        const cardsJsx = cards.map((card) => (
          <div key={card.postId}>
            <PostMiniCardBase
              card={card}
              onClick={() => onSelect(card)}
              onClose={() => onClose(card)}
              onDuplicate={() => onDuplicate?.(card)}
              showDuplicate={!isReachedMaxFormCount}
              isClustered={cards.length > 1}
            />
          </div>
        ));

        if (cards.length <= 1) {
          return (
            <div
              className="flex flex-col gap-[8px]"
              key={cards[0]?.clusterId || idx}
            >
              {cardsJsx}
            </div>
          );
        }

        return (
          <div
            className="flex flex-col pl-[8px] pr-[8px] pt-[8px] pb-[4px] mr-[-8px] gap-[4px] shadow-md rounded-l-[8px] bg-blue-50"
            key={cards[0]?.clusterId || idx}
            style={{
              clipPath: "inset(-10px 0px -10px -10px)",
            }}
          >
            <div
              className="flex gap-[4px] items-baseline text-blue-500 text-[12px] cursor-pointer"
              onClick={() => {
                if (collapsedClusterIds.includes(cards[0]?.clusterId)) {
                  setCollapsedClusterIds(
                    collapsedClusterIds.filter(
                      (id) => id !== cards[0]?.clusterId
                    )
                  );
                } else {
                  setCollapsedClusterIds([
                    ...collapsedClusterIds,
                    cards[0]?.clusterId,
                  ]);
                }
              }}
            >
              <i className="fa fa-hexagon-nodes text-[12px]" />
              <span className="font-semibold">Cluster</span>
              <i
                className={`fa ${collapsedClusterIds.includes(cards[0]?.clusterId) ? "fa-chevron-down" : "fa-chevron-up"} text-[12px]`}
              />
            </div>
            <div
              className={`flex flex-col gap-[8px] mb-[4px] ${collapsedClusterIds.includes(cards[0]?.clusterId) ? "hidden" : ""}`}
            >
              {cardsJsx}
            </div>
          </div>
        );
      })}
    </div>
  );
};

function partitionByCluster(cards: Card[]) {
  const clusters = new Map<string, Card[]>();
  for (const card of cards) {
    const clusterId = card.clusterId || card.postId;
    if (!clusters.has(clusterId)) {
      clusters.set(clusterId, []);
    }
    clusters.get(clusterId)!.push({
      ...card,
      clusterId,
    });
  }
  return [...clusters.values()];
}
